<script setup lang="ts">
//#region imports
import { computed, nextTick, ref, useTemplateRef, onBeforeUnmount, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useApi } from '@/api';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useEventBus } from '@/setup/eventBus';
import { useTypedStore } from '@/store';
import _ from '@/setup/lodash';
import Notes from '@/components/Notes.vue';
import { usePermissions } from '@/composables/usePermissions';
import { useFormatters } from '@/composables/useFormatters';
import { useCopy } from '@/composables/useCopy';
import UiPagination from '@/components/UIElements/UiPagination.vue';
import ExportData from '@/components/ExportData.vue';
import { useTablePaginator, useTableSort } from '@/composables/useTableLS';
import { useLs } from '@/setup/vueLs';
import updateUrl from '@/service/updateUrl';
import { useDetectPermissions } from '@/composables/useDetectPermissions';
import { useFilters } from '@/setup/filters';
import type { UiTableNewField , UiTableNewSortEmit } from '@/types/table';
import type { GetPaymentsHistoryByIdCpaPlayersData, GetPaymentsHistoryByIdCpaPlayersResponse, GetPaymentsHistoryByIdResponse } from '@/types/openapi-ts';
import type { CardItemType } from '@/components/CardComponents/CardItem.vue';
//#endregion

//#region types
type PaymentMethods = Extract<keyof typeof api, 'declinePayment' | 'reviewPayment' | 'reworkedPayment' | 'approvePayment' | 'manualPayout'>;

type PaymentsHistory = GetPaymentsHistoryByIdResponse['payload'];
type PaymentsHistoryPermissions = NonNullable<GetPaymentsHistoryByIdResponse['misc']>['permissions'];
type PaymentsHistoryPlayers = GetPaymentsHistoryByIdCpaPlayersResponse['payload'];
type PaymentsHistoryPlayersRow = PaymentsHistoryPlayers['data'][number];
type PaymentsHistoryPlayersExportUrls = NonNullable<GetPaymentsHistoryByIdCpaPlayersResponse['misc']>['export_urls'];
//#endregion


//#region const
const LS_PATH = 'Payments/Info';

const sizes = {
  1080: 20,
  1440: 30,
};

const playersFields: UiTableNewField<PaymentsHistoryPlayersRow>[] = [
  {
    name: 'site_player_id',
    minWidth: 150,
    align: 'left',
    sortable: 'custom',
    link: getPlayerLink,
    action: () => {},
    controlType: 'icon',
    controlColorIco: () => 'var(--text-negative)',
    actionName: (row) => (row.is_blocked ? 'lock' : ''),
    tooltip: 'players.info.blocked_by_security_service',
  },
  {
    name: 'external_id',
    minWidth: 110,
    align: 'left',
    sortable: 'custom',
  },
  {
    name: 'registration_timestamp',
    minWidth: 110,
    align: 'left',
    sortable: 'custom',
    format: 'date',
    formatOptions: {
      in: '',
      out: 'dd.MM.yyyy HH:mm',
    },
  },
  {
    name: 'site_registration_type_name',
    minWidth: 110,
    align: 'left',
    sortable: 'custom',
  },
  {
    name: 'first_deposit_timestamp',
    minWidth: 140,
    align: 'left',
    sortable: 'custom',
    format: 'date',
    formatOptions: {
      in: '',
      out: 'dd.MM.yyyy HH:mm',
    },
  },
  {
    name: 'conversion_timestamp',
    minWidth: 140,
    align: 'left',
    sortable: 'custom',
    format: 'date',
    formatOptions: {
      in: '',
      out: 'dd.MM.yyyy HH:mm',
    },
  },
  {
    name: 'days_since_reg',
    minWidth: 130,
    align: 'right',
    sortable: 'custom',
  },
  {
    name: 'days_active',
    minWidth: 110,
    align: 'right',
    sortable: 'custom',
  },
  {
    name: 'player_country_name',
    minWidth: 100,
    align: 'left',
    sortable: 'custom',
  },
  {
    name: 'latest_activity_timestamp',
    minWidth: 130,
    align: 'left',
    sortable: 'custom',
    format: 'date',
    formatOptions: {
      in: '',
      out: 'dd.MM.yyyy HH:mm',
    },
  },
  {
    name: 'latest_deposit_timestamp',
    minWidth: 130,
    align: 'left',
    sortable: 'custom',
    format: 'date',
    formatOptions: {
      in: '',
      out: 'dd.MM.yyyy HH:mm',
    },
  },
  {
    name: 'top_deposits_payment_system_name',
    minWidth: 170,
    align: 'left',
    sortable: 'custom',
  },
  {
    name: 'top_withdrawals_payment_system_name',
    minWidth: 170,
    align: 'left',
    sortable: 'custom',
  },
  {
    name: 'chargebacks_count',
    minWidth: 130,
    align: 'right',
    sortable: 'custom',
  },
  {
    name: 'chargebacks_sum',
    minWidth: 130,
    align: 'right',
    sortable: 'custom',
    format: '-formatMoney',
    class: 'negative',
  },
  {
    name: 'deposits_count',
    minWidth: 130,
    align: 'right',
    sortable: 'custom',
  },
  {
    name: 'deposits_sum',
    minWidth: 120,
    align: 'right',
    sortable: 'custom',
    format: 'formatMoney',
  },
  {
    name: 'withdrawals_count',
    minWidth: 150,
    align: 'right',
    sortable: 'custom',
  },
  {
    name: 'withdrawals_sum',
    minWidth: 150,
    align: 'right',
    sortable: 'custom',
    format: 'formatMoney',
  },
  {
    name: 'sport_bets_count',
    minWidth: 140,
    align: 'right',
    sortable: 'custom',
  },
  {
    name: 'sport_bets_sum',
    minWidth: 130,
    align: 'right',
    sortable: 'custom',
    format: 'formatMoney',
  },
  {
    name: 'avg_sport_bets_odd',
    minWidth: 150,
    align: 'right',
    sortable: 'custom',
    format: 'odd',
  },
  {
    name: 'games_bets_count',
    minWidth: 150,
    align: 'right',
    sortable: 'custom',
  },
  {
    name: 'games_bets_sum',
    minWidth: 140,
    align: 'right',
    sortable: 'custom',
    format: 'formatMoney',
  },
  {
    name: 'betting_profit',
    minWidth: 120,
    align: 'right',
    sortable: 'custom',
    format: 'formatMoney',
  },
  {
    name: 'cpa_revenue',
    minWidth: 120,
    align: 'right',
    sortable: 'custom',
    format: 'formatMoney',
  },
  {
    name: 'revshare_revenue',
    minWidth: 120,
    align: 'right',
    sortable: 'custom',
    format: 'formatMoney',
  },
  {
    name: 'revenue',
    minWidth: 100,
    align: 'right',
    sortable: 'custom',
    format: 'formatMoney',
  },
  {
    name: 'subid1',
    minWidth: 100,
    align: 'left',
    sortable: 'custom',
  },
  {
    name: 'subid2',
    minWidth: 100,
    align: 'left',
    sortable: 'custom',
  },
  {
    name: 'subid3',
    minWidth: 100,
    align: 'left',
    sortable: 'custom',
  },
  {
    name: 'subid4',
    minWidth: 100,
    align: 'left',
    sortable: 'custom',
  },
  {
    name: 'subid5',
    minWidth: 100,
    align: 'left',
    sortable: 'custom',
  },
  {
    name: 'total_profit',
    minWidth: 100,
    align: 'right',
    sortable: 'custom',
    format: 'formatMoney',
    computedClass(value) {
      return value < 0 ? 'negative' : '';
    },
  },
];

//#endregion

//#region composables
const api = useApi();
const route = useRoute();
const router = useRouter();
const store = useTypedStore();
const popupManager = usePopupManager();
const eventBus = useEventBus();
const { t } = useI18n();
const { hasPermission } = usePermissions();
const { formatCurrencyValue } = useFormatters();
const { copy } = useCopy();
const { checkRequest, checkRoute } = useDetectPermissions();
const ls = useLs();
const filters = useFilters();

const { limit, count, page, setPage, setLsPathPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 10,
});

const { sortProp, sortOrder, setSort, optionsSort, setLsPath } = useTableSort({
  initialOptions:{
    sort_column: 'site_player_id',
    sort_dir: 'asc',
  },
  lsPath: LS_PATH,
  fields: playersFields,
});
//#endregion

//#region state
const isDataLoading = ref(true);
const isPlayersLoading = ref(false);
const isFirstLoading = ref(true);

const exportUrls = ref({} as PaymentsHistoryPlayersExportUrls);
const selectGroupRef = useTemplateRef('selectGroup');
const tablePlayerRef = useTemplateRef('tablePlayer');
const currencyCode = ref('');

const playersTableData = ref<PaymentsHistoryPlayers['data']>([]);
const playerSums = ref({} as PaymentsHistoryPlayers['totals']);
const playerIds = ref<any[]>([]);

const selectedFields = ref<string[]>([]);
const hideStatsAfterConversion = ref(false);

const info = ref({} as PaymentsHistory);
const permissions = ref({} as PaymentsHistoryPermissions);
//#endregion

//#region computed
const paymentMethods = computed(() => store.getters['misc/paymentMethods']);

const configExport = computed(() => [
  {
    command: 'csv',
    label: 'CSV',
  },
  {
    command: 'xlsx',
    label: 'XLSX',
  },
  {
    command: 'csv_network',
    label: `CSV ( ${t('payments.info.for_network')} )`,
  },
  {
    command: 'xlsx_network',
    label: `XLSX ( ${t('payments.info.for_network')} )`,
  },
]);

const statsFields = computed(() => {
  return [
    {
      name: 'offer_name',
      title: t('payments.info.offer'),
      align: 'left',
      minWidth: '450',
      sortable: false,
    },
    {
      name: 'uniq_players_count',
      title: t('payments.info.table_players'),
      align: 'right',
      headerAlign: 'right',
      width: '100',
      minWidth: '100',
      sortable: false,
    },
    {
      name: 'revshare_revenue',
      title: `${t('payments.info.rev_share')}`,
      align: 'right',
      headerAlign: 'right',
      width: '110',
      minWidth: '110',
      sortable: false,
      format: 'formatMoney',
      computedClass: getCurrencyClass,
    },
    {
      name: 'cpa_revenue',
      title: `${t('payments.info.cpa')}`,
      align: 'right',
      headerAlign: 'right',
      width: '110',
      minWidth: '110',
      sortable: false,
      format: 'formatMoney',
      computedClass: getCurrencyClass,
    },
    {
      name: 'revenue',
      title: `${t('payments.players.fields.total_profit.title')}`,
      align: 'right',
      headerAlign: 'right',
      width: '120',
      minWidth: '120',
      sortable: false,
      format: 'formatMoney',
      computedClass: getCurrencyClass,
    },
  ];
});

const paymentId = computed(() => {
  return route.params.id as string;
});

const chargeActions = computed(() => {
  return calcPermissions('chargebacks_actions');
});

const fileAttachmentAllowed = computed(() => {
  return info.value.type === 'external'
    ? true
    : paymentMethods.value && info.value.site_payment_method_id
      ? checkPaymentMethodProperty('file_attachment_allowed')
      : false;
});

const paymentsInformationList = computed(() => {
  let result: CardItemType[] = [
    {
      name: t('payments.info.approved_by'),
      value:  info.value.approved_by_admin_name,
    },
  ];

  if(info.value.affiliate_referred_by_info) {
    result.unshift({
      name: t('payments.info.introduced_by'),
      value: info.value.affiliate_referred_by_info.email,
      valueLink: _.get(info.value, 'affiliate_referred_by_info.permissions.view', false) ? `/affiliates/${info.value.affiliate_referred_by_info?.id}/info` : null,
    });
  }

  if(info.value.team_name) {
    result.push({
      name: t('payments.info.team'),
      value:  info.value.team_name,
    });
  }

  return result;
});

const fromPage = computed(() => {
  return route.query.from;
});

const backPage = computed(() => {
  if (fromPage.value === 'external_payments') {
    return {
      title: t('payments.page_title.external_payments'),
      link: '/payments/external',
    };
  }

  if(fromPage.value === 'payments_moderation' || info.value.moderation_status !== 'approved') {
    return {
      title: `${t('payments.page_title.payments_moderation.name')} — ${t(`payments.page_title.payments_moderation.${info.value.moderation_status}`)}`,
      link: `/payments/moderation/${info.value.moderation_status ? info.value.moderation_status : '' }`,
    };
  }

  return {
    title: `${t('payments.page_title.payments_payout.name')} — ${t(`payments.page_title.payments_payout.${info.value.payout_status}`)}`,
    link: `/payments/payout/${info.value.payout_status}`,
  };
});

const breadcrumbs = computed(() => {
  return [
    backPage.value,
    {
      title: info.value.affiliate_email,
    },
  ];
});

const pPaymentPeriod = computed(() => {
  return info.value.payment_period_start && info.value.payment_period_end
    ? `${filters.formatDate4(info.value.payment_period_start)} - ${filters.formatDate4(info.value.payment_period_end)}`
    : '';
});

const isDisabledButton = computed(() => {
  const method = info.value.site_payment_method_id;
  const attachmentAllowed = paymentMethods.value && method ? checkPaymentMethodProperty('file_attachment_allowed') : false;
  const attachmentRequired = paymentMethods.value && method ? checkPaymentMethodProperty('file_attachment_required') : false;
  return (attachmentAllowed && attachmentRequired && !info.value.files.length) || method === null;
});
//#endregion

//#region methods
function initData() {
  if (!paymentId.value) {
    router.push('/payments');
  }

  if (checkRequest('payments_view')){
    const _urlData = updateUrl.getParseParams(route.hash);
    const _defaultColumns = [
      'site_player_id',
      'registration_timestamp',
      'site_registration_type_name',
      'deposits_count',
      'deposits_sum',
      'withdrawals_sum',
      'betting_profit',
      'cpa_revenue',
      'revshare_revenue',
      'chargebacks_sum',
      'total_profit',
    ];

    if (!_.isEmpty(_.omit(_urlData, 'from'))) {
      setPage(_urlData);

      delete _urlData.page;
      delete _urlData.limit;

      _.merge(optionsSort.value, _urlData);

      setSort(_urlData);
      selectedFields.value = Object.values(_.get(_urlData, ['columns']) as string[] || _defaultColumns);
      hideStatsAfterConversion.value = _.get(_urlData, ['hide_stats_after_conversion']) === 'true';
    } else {
      selectedFields.value = JSON.parse(ls.get(`${LS_PATH}/columns`)) || _defaultColumns;
    }

    nextTick(() => {
      fetchData();
    });
  }
  checkRoute('payments_view');
}

async function fetchData(silent = false) {
  if (!silent) {
    isDataLoading.value = true;
  }

  try {
    const { data: { payload, misc } } = await api.getPaymentInfo(paymentId.value);
    permissions.value = misc!.permissions;
    info.value = payload;

    await nextTick();
    getPlayers();

  } finally {
    isDataLoading.value = false;
  }
}

const _query = () => {
  return {
    ...optionsSort.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
    hide_stats_after_conversion: hideStatsAfterConversion.value,
  } as GetPaymentsHistoryByIdCpaPlayersData['query'];
};

const _completedFilterUrl = () => {
  const _dataFilters = {
    ..._.omit(_query(), ['offset']),
    page: page.value,
    columns: selectedFields.value,
    from: fromPage.value,
  };
  updateUrl.updateGetParams(_dataFilters);
};

async function getPlayers(silent = false) {
  if (!silent && !isFirstLoading.value) {
    isPlayersLoading.value = true;
  }

  _completedFilterUrl();
  let options = _query();

  try {
    const { data: { payload, misc } } = await api.getPaymentInfoPlayers(paymentId.value, options);

    exportUrls.value = misc!.export_urls;
    count.value = misc!.count;
    currencyCode.value = misc!.currency_code;
    playersTableData.value = payload.data;

    playerSums.value = payload.totals;
    playerSums.value.registration_timestamp = '';
    playerSums.value.latest_activity_timestamp = '';
    playerSums.value.player_country_name = '';
  } finally {
    if(!isFirstLoading.value) {
      isPlayersLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  }
}

function calcPermissions(item: keyof typeof permissions.value) {
  return permissions.value[item];
}

async function changeStatus(method: PaymentMethods) {
  try {
    await api[method](paymentId.value);
    fetchData();
  } catch (error: any) {
    eventBus.$emit('notify-event', {
      type: 'error',
      text: t('payments.noty.req_fail', { statusCode: error.status }),
    });
  }
}

function declineHandler() {
  const context = popupManager.confirm.open({
    data: {
      title: t('payments.confirm.title_2'),
      message: t('payments.confirm.msg_2', { affiliateEmail: info.value.affiliate_email }),
      bodyIcon: 'exclamation-triangle',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('payments.confirm.action_2'),
      confirmType: 'negative',
      confirmIcon: 'check',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const confirm = await context;
        await changeStatus('declinePayment');
        confirm.handleClose();
      },
    },
  });
}

function reviewHandler() {
  const context = popupManager.confirm.open({
    data: {
      title: t('payments.confirm.title_3'),
      message: t('payments.confirm.msg_3'),
      bodyIcon: 'exclamation-triangle',
      confirmTitle: t('payments.confirm.action_3'),
      confirmType: 'positive',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const confirm = await context;
        await changeStatus('reviewPayment');
        confirm.handleClose();
      },
    },
  });
}

function reworkedHandler() {
  const context = popupManager.confirm.open({
    data: {
      title: t('payments.confirm.title_7'),
      message: t('payments.confirm.msg_7'),
      bodyIcon: 'exclamation-triangle',
      confirmTitle: t('payments.confirm.action_7'),
      confirmType: 'positive',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const confirm = await context;
        await changeStatus('reworkedPayment');
        confirm.handleClose();
      },
    },
  });
}

function approveHandler() {
  const context = popupManager.confirm.open({
    data: {
      title: t('payments.confirm.title_1'),
      message: t('payments.confirm.msg_1', { affiliateEmail: info.value.affiliate_email }),
      bodyIcon: 'exclamation-triangle',
      confirmTitle: t('payments.confirm.action_1'),
      confirmType: 'positive',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const confirm = await context;
        await changeStatus('approvePayment');
        confirm.handleClose();
      },
    },
  });
}

function paidManuallyHandler() {
  const context = popupManager.confirm.open({
    data: {
      title: t('payments.confirm.title_6'),
      message: t('payments.confirm.msg_6', { affiliateEmail: info.value.affiliate_email }),
      bodyIcon: 'exclamation-triangle',
      confirmTitle: t('payments.confirm.action_6'),
      confirmType: 'positive',
      confirmIcon: 'check',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const confirm = await context;

        await changeStatus('manualPayout');
        confirm.handleClose();
      },
    },
  });
}

function checkPaymentMethodProperty(property: keyof (typeof paymentMethods.value)[number]) {
  if (!paymentMethods.value.length) return false;
  const pmProperty = _.find(paymentMethods.value, (item) => item.id === info.value.site_payment_method_id);
  return pmProperty ? pmProperty[property] : false;
}

function handlerAttachFilePopup() {
  popupManager.uploadFilesPopup.open({
    data: {
      paymentId: info.value.id,
      uploadedFiles: info.value.files,
    },
    listeners: {
      success: () => {
        fetchData();
      },
    },
  });
}

function getCurrencyClass(value: number) {
  return value < 0 ? 'negative' : '';
}

function exportPlayers(format: string) {
  if (exportUrls.value) {
    window.open(exportUrls.value[format as keyof typeof exportUrls.value]);
  }
}

function getPlayerLink(row: PaymentsHistoryPlayersRow) {
  return hasPermission('players_view') ? `/players/${row.player_id}/info` : '';
}

function openColumnsSelect() {
  selectGroupRef.value?.uiSelectElement?.toggleMenu?.();
}

function handleSelectColumns() {
  ls.set(`${LS_PATH}/columns`, JSON.stringify(selectedFields.value));
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    getPlayers();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    getPlayers();
  });
};

function changeLocaleGlobal() {
  getPlayers(true);
}

function clearCheck() {
  tablePlayerRef.value?.clearCheck?.();
  tablePlayerRef.value?.changeRow?.('alldel');
}

function chargePlayer(player: PaymentsHistoryPlayers['data'][number]) {
  api.chargePaymentCpaRevenue(paymentId.value, player.player_id)
    .then(() => {
      eventBus.$emit('notify-event', {
        type: 'info',
        text: t('payments.noty.charge_soon'),
      });
      clearCheck();
      fetchData(true);
    });
}
//#endregion

//#region hooks
initData();

onBeforeMount(() => {
  eventBus.$on('changeLocaleGlobal', changeLocaleGlobal);
});

onBeforeUnmount(() => {
  eventBus.$off('changeLocaleGlobal', changeLocaleGlobal);
});

function checkedRows(id: string, ids: string[]) {
  playerIds.value = ids;
}

function massCharge() {
  return api.massChargePaymentCpaRevenue(paymentId.value, playerIds.value as any).then(() => {
    fetchData();
    clearCheck();
  });
}

function massChargeHandler() {
  const context = popupManager.confirm.open({
    data: {
      title: t('payments.confirm.title_5'),
      message: t('payments.confirm.msg_5', { id: playerIds.value.length }),
      bodyIcon: 'exclamation-triangle',
      confirmTitle: t('payments.confirm.action_5'),
      confirmType: 'positive',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const confirm = await context;
        await massCharge();
        confirm.handleClose();
      },
    },
  });
}
//#endregion
</script>

<template>
  <div
    v-loading="isDataLoading"
    class="payment-info"
  >
    <div class="t:space-y-16">
      <Breadcrumbs :items="breadcrumbs" />
      <div class="t:flex t:items-center t:gap-8">
        <div class="payment-info__title-info">
          ID: {{ info.incrementing_id }}
        </div>
        <UiSeparator type="dot" />
        <div class="payment-info__title-name">
          {{ $t('payments.info.payment_for') }} {{ info.affiliate_email }}
        </div>
        <template v-if="info.payment_period_start && info.payment_period_end">
          <UiSeparator type="dot" />
          <div class="payment-info__title-info">
            {{ $filters.formatDate4(info.payment_period_start) }} - {{ $filters.formatDate4(info.payment_period_end) }}
          </div>
        </template>
      </div>
      <div
        v-if="calcPermissions('review_actions') && (info.moderation_status === 'on_review' || ( info.moderation_status === 'approved' && (info.payout_status === 'not_paid' || info.payout_status === 'for_rework')))"
        class="t:flex t:items-center t:gap-8"
      >
        <template v-if="info.moderation_status === 'on_review'">
          <UiTooltip
            :tooltip="info.site_payment_method_id !== null ? $t('payments.info.approve_tooltip') : $t('payments.info.approve_wallet')"
            :disabled="!isDisabledButton"
            placement="bottom"
          >
            <UiButton
              filled
              icon="check"
              :disabled="isDisabledButton"
              @click="approveHandler"
            >
              {{ $t('payments.info.approve') }}
            </UiButton>
          </UiTooltip>
          <UiButton
            icon="money-bill-wave"
            :disabled="isDisabledButton"
            @click="paidManuallyHandler"
          >
            {{ $t('payments.info.paidManually') }}
          </UiButton>
        </template>
        <template v-if="info.moderation_status === 'approved'">
          <UiButton
            v-if="info.payout_status === 'not_paid'"
            filled
            icon="check"
            @click="reviewHandler"
          >
            {{ $t('payments.info.to_review') }}
          </UiButton>
          <UiButton
            v-if="info.payout_status === 'for_rework'"
            filled
            icon="check"
            @click="reworkedHandler"
          >
            {{ $t('payments.info.reworked') }}
          </UiButton>
        </template>
        <UiButton
          icon="times"
          type="negative"
          @click="declineHandler"
        >
          {{ $t('payments.info.decline') }}
        </UiButton>
      </div>
      <UiSeparator />
      <div class="t:flex t:gap-32">
        <div class="payment-info__block t:gap-24">
          <div class="t:flex t:gap-24 t:relative">
            <div class="payment-info__block payment-info__block_side t:space-y-24">
              <CardBlock
                :title="$t(`mediaBuying.info.account_overview.title`)"
                :list="[
                  {
                    name: $t(`payments.info.available`),
                    value: formatCurrencyValue(info.balance_currency_code, info.available_balance),
                    valueClass: 'payment-info__item-value payment-info__item-value_positive',
                  },
                  {
                    name: $t(`payments.info.in_hold`),
                    value: formatCurrencyValue(info.balance_currency_code, info.hold_balance),
                    valueClass: 'payment-info__item-value payment-info__item-value_negative',
                  }
                ]"
              />
              <CardBlock
                :title="$t(`payments.info.information`)"
                :list="paymentsInformationList"
              />
            </div>
            <div class="payment-info__block ">
              <CardBlock
                :title="$t(`payments.info.payment_info`)"
              >
                <template v-slot:actionOuter>
                  <div
                    v-if="info.moderation_status && info.payout_status"
                    class="t:flex t:gap-8"
                  >
                    <UiStatusBlock
                      :title="$t(`mediaBuying.status.moderation`)"
                      :status="info.moderation_status"
                      :status-text="$t(`mediaBuying.status.${info.moderation_status}`)"
                    />
                    <UiStatusBlock
                      :title="$t(`mediaBuying.status.payout`)"
                      :status="info.payout_status"
                      :status-text="$t(`payments.payout.${info.payout_status}`)"
                    />
                  </div>
                </template>
                <template v-slot:cardItem>
                  <CardItem no-border>
                    <template v-slot:value>
                      <div class="payment-info__request">
                        <div class="t:space-y-4">
                          <div
                            class="payment-info__request-date"
                            v-html="$t(`payments.info.created_at`, {date: $filters.formatDate3(info.created_at || '')})"
                          />
                          <div
                            class="payment-info__request-amount"
                            :class="{ 'payment-info__request-amount_negative': info.amount < 0 }"
                          >
                            {{ formatCurrencyValue(info.currency_code, info.amount) }}
                          </div>
                          <div class="payment-info__affiliate">
                            {{ $t(`payments.info.to`) }}
                            <RouterLink
                              v-if="calcPermissions('edit_affiliate')"
                              :to="`/affiliates/${info.affiliate_id}/info`"
                              class="payment-info__link"
                            >
                              {{ info.affiliate_email }}
                            </RouterLink>
                            <span v-else> {{ info.affiliate_email }} </span>
                          </div>
                        </div>
                      </div>
                    </template>
                  </CardItem>
                  <template v-if="info.type !== 'external'">
                    <CardItem
                      :item="{
                        name: $t(`affiliates.affiliatesInfo.method`),
                        value: info.site_payment_method_name,
                      }"
                    >
                      <template
                        v-if="info.site_payment_method_logo_base64"
                        v-slot:value
                      >
                        <img
                          :src="info.site_payment_method_logo_base64"
                          :alt="info.site_payment_method_name!"
                          class="payment-info__method-img"
                        >
                      </template>
                    </CardItem>
                    <CardItem
                      :item="{
                        name: $t(`affiliates.affiliatesInfo.wallet`),
                      }"
                    >
                      <template
                        v-if="info.payment_wallet"
                        v-slot:value
                      >
                        <div
                          class="payment-info__item-value payment-info__item-value_positive t:cursor-pointer"
                          @click="copy(info.payment_wallet)"
                        >
                          {{ info.payment_wallet }}
                          <UiIcon
                            color="inherit"
                            name="clone"
                          />
                        </div>
                      </template>
                    </CardItem>
                    <CardItem
                      :item="{
                        name: $t(`affiliates.affiliatesInfo.period`),
                        value: pPaymentPeriod
                      }"
                    />
                  </template>
                  <CardItem v-if="info.additional_payout_information">
                    <template v-slot:value>
                      <div class="payment-info__description">
                        <div class="payment-info__description-title">
                          {{ $t(`payments.info.add_information`) }}
                        </div>
                        {{ info.additional_payout_information }}
                      </div>
                    </template>
                  </CardItem>
                </template>
              </CardBlock>
            </div>
          </div>
          <template v-if="info.type !== 'external'">
            <UiSeparator class="t:mt-24" />
            <UiToolbar>
              <template v-slot:title>
                {{ $t('payments.info.statistics') }}
              </template>
            </UiToolbar>
            <UiTableNew
              v-if="!_.isEmpty(info)"
              :fields="statsFields"
              :data="info?.affiliate_stats?.data"
              :currency="info.currency_code"
              is-static
              show-total
              disable-sort
              :total="info?.affiliate_stats?.totals || []"
            />
          </template>
        </div>
        <div
          v-if="fileAttachmentAllowed || hasPermission('affiliates_notes_view')"
          class="payment-info__block payment-info__block_side t:relative"
        >
          <div
            class="payment-info__wrapper t:space-y-24"
            :class="{ 'payment-info__wrapper_static': info.type === 'external' }"
          >
            <FilesList
              v-if="fileAttachmentAllowed"
              :files="info.files"
              :can-upload="hasPermission('payments_review_actions') && (info.moderation_status === 'on_review' || (info.moderation_status === 'approved' && info.payout_status === 'for_rework'))"
              @attach-file="handlerAttachFilePopup"
            />
            <Notes
              v-if="hasPermission('affiliates_notes_view')"
              :affiliate-id="info.affiliate_id"
              :add-note-permission="calcPermissions('edit_affiliate') && hasPermission('affiliates_notes_edit')"
            />
          </div>
        </div>
      </div>
      <template v-if="info.type !== 'external'">
        <UiSeparator class="t:mt-32" />
        <UiToolbar>
          <template v-slot:title>
            {{ $t('payments.info.players') }}
          </template>
          <template v-slot:left-slot>
            <div
              v-if="playerIds.length > 0"
              class="payment-info__mass-charge"
            >
              {{ `${$t('payments.info.players_selected', playerIds.length)}` }}
              <div class="t:flex t:items-center t:space-x-8">
                <UiAction
                  icon="times"
                  color="var(--text-negative)"
                  :action-name="$t('payments.info.cancel')"
                  @click="clearCheck()"
                />
                <UiSeparator type="vertical" />
                <UiAction
                  icon="money-bill"
                  color="var(--text-positive)"
                  :action-name="$t('payments.info.charge')"
                  @click="massChargeHandler()"
                />
              </div>
            </div>
          </template>
          <template v-slot:right-slot>
            <UiCheckbox
              v-model="hideStatsAfterConversion"
              :label="$t('payments.info.hide_conversion')"
              @change="getPlayers"
            />
            <UiPagination
              :page="page"
              :page-size="limit"
              :count="count"
              show-size-select
              @page-change="pageChange"
            />
          </template>
          <template v-slot:actions>
            <div class="t:relative">
              <UiButton
                is-icon
                icon="eye"
                @click="openColumnsSelect"
              />
              <UiSelectElement
                ref="selectGroup"
                v-model="selectedFields"
                class="payment-info__columns-select"
                multiple
                @change="handleSelectColumns"
              >
                <template v-slot:options>
                  <ElOption
                    v-for="item in playersFields"
                    :key="item.name"
                    :label="`${$t(`payments.players.fields.${item.name}.title`)}`"
                    :value="item.name"
                  />
                </template>
              </UiSelectElement>
            </div>
            <ExportData
              :data="configExport"
              @export-data="exportPlayers"
            />
          </template>
        </UiToolbar>
        <UiTableNew
          ref="tablePlayer"
          v-loading="isPlayersLoading"
          :fields="_.filter(playersFields, field => selectedFields.indexOf(field.name) !== -1)"
          :data="playersTableData"
          :rows-count="limit"
          :currency="currencyCode"
          :sort="{
            prop: sortProp,
            order: sortOrder,
          }"
          :total="playerSums"
          :checked-row="chargeActions && info.moderation_status === 'on_review'"
          :show-check-all="true"
          hidden-check-box="chargeback"
          checked-row-id="player_id"
          lazy-loading
          is-static
          show-total
          i18n-path="payments.players"
          @checked-rows="checkedRows"
          @sort="handleSort"
        >
          <template v-slot:append>
            <ElTableColumn
              v-if="chargeActions && info.moderation_status === 'on_review'"
              :width="76"
              :resizable="false"
              fixed="right"
              align="center"
              header-align="center"
            >
              <template v-slot:header="scope">
                <div
                  :key="scope.$index"
                  class="table-action-header"
                >
                  {{ $t('settings.tokens.actions') }}
                </div>
              </template>
              <template v-slot="scope">
                <ActionIcon
                  v-if="scope.row.chargebacks_count === 0"
                  icon="money-bill"
                  color="var(--text-negative)"
                  :tooltip="$t('payments.info.charge')"
                  @click="chargePlayer(scope.row)"
                />
              </template>
            </ElTableColumn>
          </template>
        </UiTableNew>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
.payment-info {
  $root: &;

  padding-bottom: 40px;

  &__title-info {
    color: var(--text-body-secondary);
    font-size: 20px;
    font-weight: 500;
  }
  &__title-name {
    color: var(--text-body);
    font-size: 24px;
    font-weight: 500;
  }

  &__item-value {
    font-size: 14px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 4px;
    &_positive {
      color: var(--text-positive);
    }
    &_negative {
      color: var(--text-negative);
    }
  }

  &__link {
    color: var(--text-link);
  }

  &__block {
    flex-basis: 100%;
    &_side {
      flex-shrink: 0;
      flex-basis: 300px;
      width: 300px;
    }
  }

  &__request-date {
    color: var(--text-body-secondary);
    font-size: 12px;
    span {
      color: var(--text-body);
    }
  }
  &__request-amount {
    color: var(--text-positive);
    font-size: 24px;
    font-weight: 500;
    line-height: 140%;
    &_negative {
      color: var(--text-negative);
    }
  }
  &__affiliate {
    color: var(--text-body-secondary);
    font-size: 14px;
    span {
      color: var(--text-body);
    }
  }
  &__description-title {
    color: var(--text-body-secondary);
    font-size: 12px;
  }

  &__wrapper {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;

    &_static {
      position: static;
    }
  }
  &__columns-select {
    position: absolute;
    top: 0;
    opacity: 0;
    visibility: hidden;
  }
  &__mass-charge {
    display: flex;
    padding: 8px;
    align-items: center;
    gap: 32px;

    border-radius: 5px;
    border: 1px solid var(--border-focused);
    background: var(--background-brand-subtle);
    font-weight: 500;

    font-size: 12px;
  }
  &__method-img {
    max-width: 100px;
    max-height: 28px;
  }
  &__information-title {
    white-space: nowrap;
  }
  &__information-value {
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
}
</style>
